import {
  WEEKLY_PLAN_DAYS,
  weeklyPlanPdfFilename,
  type WeeklyPlanDay,
  type WeeklyPlanToday,
} from '@gb-ticket/shared-types';
import { ChevronDownIcon, ChevronRightIcon, Loader2Icon, Trash2Icon, XIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useBeforeUnload, useBlocker, useParams, useSearchParams } from 'react-router';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePageTitle } from '@/hooks/use-page-title';
import { cn } from '@/lib/utils';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useMe } from '@/platform/auth/use-me';
import {
  useIntranetColleagues,
  useIntranetModule,
  usePublishWeeklyPlan,
  useSaveWeeklyPlan,
  useWeeklyPlanWeek,
} from '@/products/intranet/features/intranet/use-intranet-directory';
import { WeeklyPlanGroupList } from '@/products/intranet/pages/weekly-plan-group-list';
import {
  readWeeklyPlanStaff,
  writeWeeklyPlanStaff,
} from '@/products/intranet/pages/weekly-plan-staff';
import {
  CARD_CLASS,
  DAY_LABEL,
  DAY_SHORT,
  TIME_ROW_CLASS,
  assignmentField,
  boardSnapshot,
  cellField,
  dayHeaderClass,
  isRecentCell,
  personChrome,
  portalPeople,
  rowTitle,
  splitDisplayName,
  toDraft,
  toGroups,
  type DraftGroup,
  type DraftRow,
} from '@/products/intranet/pages/weekly-plan-fields';
import {
  timeColumns,
  type TimeColumnReason,
  type WeeklyPlanRange,
} from '@/products/intranet/pages/weekly-plan-view';

const TIME_HINT: Record<TimeColumnReason, string> = {
  'outside-week': 'Heute liegt nicht in dieser Woche.',
  'no-column': 'Heute hat keine Spalte.',
};

function berlinStamp(iso: string): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${value('day')}.${value('month')}.${value('year')} ${value('hour')}:${value('minute')}`;
}

/** Current Wochenplan. A Schreibgruppe saves every Standort in one step. */
export function WeeklyPlanPage() {
  const { instanceId = '' } = useParams();
  const [params, setSearchParams] = useSearchParams();
  const yearParam = params.get('jahr');
  const weekParam = params.get('woche');
  const siteValue = params.get('standort')?.trim() ?? '';
  const siteParam = siteValue.length > 0 ? siteValue : undefined;
  const isoYear = yearParam !== null && weekParam !== null ? Number(yearParam) : undefined;
  const isoWeek = yearParam !== null && weekParam !== null ? Number(weekParam) : undefined;
  const instance = useIntranetModule(instanceId);
  const week = useWeeklyPlanWeek(instanceId, isoYear, isoWeek, siteParam);
  const me = useMe();
  const meId = me.data?.id;
  const staffStorage = typeof localStorage === 'undefined' ? null : localStorage;
  const staffScope = meId && instanceId ? `${meId}:${instanceId}` : null;
  const [staffScopeLoaded, setStaffScopeLoaded] = useState<string | null>(null);
  const savePlan = useSaveWeeklyPlan(instanceId, isoYear, isoWeek, siteParam);
  const publishPlan = usePublishWeeklyPlan(instanceId, isoYear, isoWeek, siteParam);
  const title = instance.data?.label ?? 'Wochenplan';
  usePageTitle(title);

  const linkedSite =
    siteParam && week.data?.registers.some((register) => register.siteId === siteParam)
      ? siteParam
      : null;
  const selected = linkedSite ?? week.data?.selectedSiteId ?? week.data?.registers[0]?.siteId ?? '';
  const [registerId, setRegisterId] = useState<string | null>(null);
  const activeRegister = registerId ?? selected;
  const signature = JSON.stringify({
    rows: week.data?.rows ?? null,
    groups: week.data?.groups ?? null,
  });
  const [synced, setSynced] = useState(signature);
  const [draft, setDraft] = useState<DraftRow[]>(() => toDraft(week.data?.rows ?? []));
  const [groups, setGroups] = useState<DraftGroup[]>(() => toGroups(week.data?.groups ?? []));
  const [personQuery, setPersonQuery] = useState('');
  const [freeName, setFreeName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [view, setView] = useState<'list' | 'time'>('time');
  const [range, setRange] = useState<WeeklyPlanRange>('today');
  const [dayFocus, setDayFocus] = useState<WeeklyPlanDay | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [staffOpen, setStaffOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [staffQuery, setStaffQuery] = useState('');
  const [memberQuery, setMemberQuery] = useState('');
  const [membersOpen, setMembersOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [pdfPending, setPdfPending] = useState(false);
  const loadedDraft = toDraft(week.data?.rows ?? []);
  const loadedGroups = toGroups(week.data?.groups ?? []);
  const loadedIds = new Set(
    (week.data?.rows ?? []).flatMap((row) => (row.userId ? [row.userId] : [])),
  );
  if (staffScope && staffScope !== staffScopeLoaded && meId) {
    setStaffScopeLoaded(staffScope);
    const stored = readWeeklyPlanStaff(staffStorage, meId, instanceId);
    setSelectedIds(week.data ? stored.filter((id) => loadedIds.has(id)) : stored);
  }
  if (signature !== synced) {
    setSynced(signature);
    setDraft(loadedDraft);
    setGroups(loadedGroups);
    if (!(staffScope && staffScope !== staffScopeLoaded)) {
      setSelectedIds((current) => {
        const next = current.filter((id) => loadedIds.has(id));
        return next.length === current.length ? current : next;
      });
    }
  }
  useEffect(() => {
    if (!staffScope || staffScope !== staffScopeLoaded || !meId) {
      return;
    }
    writeWeeklyPlanStaff(staffStorage, meId, instanceId, selectedIds);
  }, [staffScope, staffScopeLoaded, meId, instanceId, selectedIds, staffStorage]);
  const rows = signature === synced ? draft : loadedDraft;
  const draftGroups = signature === synced ? groups : loadedGroups;
  const dirty = week.data
    ? boardSnapshot(rows, draftGroups) !== boardSnapshot(loadedDraft, loadedGroups)
    : false;
  const blocker = useBlocker(dirty);
  useBeforeUnload(
    (event) => {
      if (dirty) {
        event.preventDefault();
      }
    },
    { capture: true },
  );
  useEffect(() => {
    if (!dirty && blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [dirty, blocker]);
  const searchReady = personQuery.trim().length >= 2;
  const colleagues = useIntranetColleagues(
    { q: personQuery.trim() || undefined, pageSize: 8 },
    Boolean(week.data?.registers.some((register) => register.writable)) && searchReady,
  );
  const memberReady = memberQuery.trim().length >= 2;
  const memberColleagues = useIntranetColleagues(
    { q: memberQuery.trim() || undefined, pageSize: 8 },
    membersOpen && memberReady,
  );

  const columnKeys: WeeklyPlanDay[] = week.data?.includeSaturday
    ? [...WEEKLY_PLAN_DAYS]
    : WEEKLY_PLAN_DAYS.filter((day) => day !== 'saturday');
  const timeSpan =
    view === 'time' ? timeColumns(week.data?.today ?? null, columnKeys, range) : null;
  const visibleColumns = timeSpan ? timeSpan.days : columnKeys;
  const timeFocus =
    visibleColumns.length === 0
      ? null
      : dayFocus && visibleColumns.includes(dayFocus)
        ? dayFocus
        : (visibleColumns[0] ?? null);
  if (view === 'time' && timeFocus && timeFocus !== dayFocus) {
    setDayFocus(timeFocus);
  }
  const timeHint = timeSpan?.reason ? TIME_HINT[timeSpan.reason] : null;
  const timeWithoutColumns = view === 'time' && visibleColumns.length === 0;
  const marked: WeeklyPlanToday | null =
    week.data?.today && columnKeys.includes(week.data.today) ? week.data.today : null;
  const listFocus: WeeklyPlanDay =
    dayFocus && columnKeys.includes(dayFocus)
      ? dayFocus
      : marked && columnKeys.includes(marked)
        ? marked
        : (columnKeys.find((day) => day === 'monday') ?? columnKeys[0] ?? 'monday');
  const writable =
    week.data?.registers.find((register) => register.siteId === activeRegister)?.writable ?? false;
  const canWriteWeek = week.data?.registers.some((register) => register.writable) ?? false;
  const registerWritable = new Map(
    (week.data?.registers ?? []).map((register) => [register.siteId, register.writable]),
  );
  const offered = searchReady
    ? (colleagues.data?.items ?? []).filter((person) => {
        const existing = rows.find((row) => row.userId === person.id);
        if (
          existing &&
          (existing.siteId === activeRegister || !registerWritable.get(existing.siteId))
        ) {
          return false;
        }
        return person.displayName.toLowerCase().includes(personQuery.trim().toLowerCase());
      })
    : [];
  const registerRows = rows.filter((row) => row.siteId === activeRegister);
  const people = portalPeople(registerRows);
  const ownRows = people.filter((row) => row.userId === meId);
  const selectedRows = people.filter(
    (row) => row.userId !== meId && selectedIds.includes(row.userId),
  );
  const staffChoices = people.filter(
    (row) => row.userId !== meId && !selectedIds.includes(row.userId),
  );
  const staffQueryText = staffQuery.trim().toLowerCase();
  const visibleStaff =
    staffChoices.length > 8
      ? staffChoices.filter((person) => person.displayName.toLowerCase().includes(staffQueryText))
      : staffChoices;
  const timeRows = [...ownRows, ...selectedRows];
  const siteGroups = draftGroups
    .filter((group) => group.siteId === activeRegister)
    .sort((left, right) => left.sortOrder - right.sortOrder);
  const planUserIds = new Set(rows.flatMap((row) => (row.userId ? [row.userId] : [])));

  function addStaff(userId: string) {
    setSelectedIds((current) => (current.includes(userId) ? current : [...current, userId]));
    setStaffOpen(false);
  }

  function removeStaff(userId: string) {
    setSelectedIds((current) => current.filter((id) => id !== userId));
  }

  function patchRow(key: string, recipe: (row: DraftRow) => DraftRow) {
    setDraft((current) => current.map((row) => (row.key === key ? recipe(row) : row)));
  }

  function addPerson(person: { id: string; displayName: string }, groupId: string | null = null) {
    const names = splitDisplayName(person.displayName);
    setDraft((current) => {
      const rest = current.filter((row) => row.userId !== person.id);
      const nextRow: DraftRow = {
        key: `person-${person.id}`,
        siteId: activeRegister,
        groupId,
        userId: person.id,
        freeName: null,
        displayName: person.displayName,
        firstName: names.firstName,
        lastName: names.lastName,
        assignment: null,
        cells: {},
      };
      if (groupId === null) {
        return [...rest, nextRow];
      }
      const last = rest.reduce(
        (index, row, position) =>
          row.siteId === activeRegister && row.groupId === groupId ? position : index,
        -1,
      );
      rest.splice(last + 1, 0, nextRow);
      return rest;
    });
    setPersonQuery('');
  }

  function removeRow(key: string) {
    setDraft((current) => current.filter((row) => row.key !== key));
  }

  function addFreeName() {
    const name = freeName.trim();
    if (name.length < 2 || name.length > 80) {
      return;
    }
    setDraft((current) => [
      ...current,
      {
        key: `free-${name}-${current.length}`,
        siteId: activeRegister,
        groupId: null,
        userId: null,
        freeName: name,
        displayName: name,
        firstName: null,
        lastName: name,
        assignment: null,
        cells: {},
      },
    ]);
    setFreeName('');
  }

  function addGroup() {
    const name = groupName.trim();
    if (name.length < 2 || name.length > 80) {
      return;
    }
    setGroups((current) => {
      const mine = current.filter((group) => group.siteId === activeRegister);
      const sortOrder = mine.reduce((max, group) => Math.max(max, group.sortOrder), -1) + 1;
      const id = crypto.randomUUID();
      return [...current, { key: id, id, siteId: activeRegister, name, sortOrder }];
    });
    setGroupName('');
  }

  function moveRow(key: string, direction: -1 | 1) {
    setDraft((current) => {
      const row = current.find((entry) => entry.key === key);
      if (!row) {
        return current;
      }
      const siblings = current.filter(
        (entry) => entry.siteId === row.siteId && entry.groupId === row.groupId,
      );
      const index = siblings.findIndex((entry) => entry.key === key);
      const swap = siblings[index + direction];
      if (!swap) {
        return current;
      }
      const next = current.slice();
      const from = next.findIndex((entry) => entry.key === key);
      const to = next.findIndex((entry) => entry.key === swap.key);
      const moved = next[from];
      next[from] = next[to]!;
      next[to] = moved!;
      return next;
    });
  }

  function moveGroup(id: string, direction: -1 | 1) {
    setGroups((current) => {
      const mine = current
        .filter((group) => group.siteId === activeRegister)
        .sort((left, right) => left.sortOrder - right.sortOrder);
      const index = mine.findIndex((group) => group.id === id);
      const swap = mine[index + direction];
      const currentGroup = mine[index];
      if (!swap || !currentGroup) {
        return current;
      }
      return current.map((group) => {
        if (group.id === id) {
          return { ...group, sortOrder: swap.sortOrder };
        }
        if (group.id === swap.id) {
          return { ...group, sortOrder: currentGroup.sortOrder };
        }
        return group;
      });
    });
  }

  function reorderGroup(id: string, overId: string) {
    setGroups((current) => {
      const mine = current
        .filter((group) => group.siteId === activeRegister)
        .sort((left, right) => left.sortOrder - right.sortOrder);
      const from = mine.findIndex((group) => group.id === id);
      const to = mine.findIndex((group) => group.id === overId);
      if (from < 0 || to < 0 || from === to) {
        return current;
      }
      const nextMine = mine.slice();
      const [moved] = nextMine.splice(from, 1);
      nextMine.splice(to, 0, moved!);
      const order = new Map(nextMine.map((group, index) => [group.id, index]));
      return current.map((group) =>
        order.has(group.id)
          ? { ...group, sortOrder: order.get(group.id) ?? group.sortOrder }
          : group,
      );
    });
  }

  function assignGroup(key: string, groupId: string | null) {
    setDraft((current) => {
      const row = current.find((entry) => entry.key === key);
      if (!row) {
        return current;
      }
      if (groupId === null) {
        return current.map((entry) => (entry.key === key ? { ...entry, groupId: null } : entry));
      }
      const nextRow = { ...row, groupId };
      const rest = current.filter((entry) => entry.key !== key);
      const last = rest.reduce(
        (index, entry, position) =>
          entry.siteId === row.siteId && entry.groupId === groupId ? position : index,
        -1,
      );
      rest.splice(last + 1, 0, nextRow);
      return rest;
    });
  }

  function dropOnRow(key: string, targetKey: string) {
    setDraft((current) => {
      const row = current.find((entry) => entry.key === key);
      const target = current.find((entry) => entry.key === targetKey);
      if (!row || !target) {
        return current;
      }
      if (row.siteId !== target.siteId) {
        return current;
      }
      const rest = current.filter((entry) => entry.key !== key);
      const to = rest.findIndex((entry) => entry.key === targetKey);
      rest.splice(to, 0, { ...row, groupId: target.groupId });
      return rest;
    });
  }

  function deleteGroup(id: string) {
    setDraft((current) =>
      current.map((row) => (row.groupId === id ? { ...row, groupId: null } : row)),
    );
    setGroups((current) => current.filter((group) => group.id !== id));
  }

  function payload() {
    if (!week.data) {
      return null;
    }
    return {
      sites: week.data.registers
        .filter((register) => register.writable)
        .map((register) => ({
          siteId: register.siteId,
          groups: draftGroups
            .filter((group) => group.siteId === register.siteId)
            .sort((left, right) => left.sortOrder - right.sortOrder)
            .map((group) => ({
              id: group.id,
              name: group.name.trim(),
              sortOrder: group.sortOrder,
            })),
          rows: rows
            .filter((row) => row.siteId === register.siteId)
            .map((row) => ({
              ...(row.id ? { id: row.id } : {}),
              userId: row.userId,
              freeName: row.freeName,
              assignment: row.assignment?.trim() ? row.assignment.trim() : null,
              groupId: row.groupId,
              cells: WEEKLY_PLAN_DAYS.flatMap((day) => {
                const text = row.cells[day]?.trim() ?? '';
                return text ? [{ day, text }] : [];
              }),
            })),
        })),
    };
  }

  async function downloadPdf() {
    if (!week.data) {
      return;
    }
    setPdfError(false);
    setPdfPending(true);
    const params = new URLSearchParams();
    params.set('jahr', String(week.data.isoYear));
    params.set('woche', String(week.data.isoWeek));
    if (activeRegister) {
      params.set('standort', activeRegister);
    }
    try {
      const response = await fetch(
        `/api/intranet/modules/${instanceId}/week/pdf?${params.toString()}`,
        { credentials: 'include' },
      );
      if (!response.ok) {
        setPdfError(true);
        return;
      }
      const blob = await response.blob();
      const header = response.headers.get('Content-Disposition') ?? '';
      const match = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i.exec(header);
      const site = week.data.registers.find((register) => register.siteId === activeRegister);
      const fallback = weeklyPlanPdfFilename({
        isoWeek: week.data.isoWeek,
        isoYear: week.data.isoYear,
        siteCount: week.data.registers.length,
        siteName: site?.name ?? '',
      });
      const filename = decodeURIComponent(match?.[1] ?? match?.[2] ?? fallback);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      setPdfError(true);
    } finally {
      setPdfPending(false);
    }
  }

  async function onSave() {
    const body = payload();
    if (!body) {
      return;
    }
    await savePlan.mutateAsync(body);
  }

  async function onPublish() {
    const body = payload();
    if (!body) {
      return;
    }
    await publishPlan.mutateAsync(body);
  }

  if (instance.isPending || week.isPending) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Wochenplan wird geladen …
      </p>
    );
  }

  if (week.isError || !week.data) {
    return (
      <section className="min-w-0 space-y-6">
        <PlatformPageHeader title={title} description="Wochenplan" />
        <p>Der Wochenplan ist nicht verfügbar.</p>
      </section>
    );
  }

  return (
    <section className="min-w-0 space-y-6">
      <PlatformPageHeader title={title} description={`Wochenplan · KW ${week.data.isoWeek}`} />
      {writable || canWriteWeek ? (
        <div className="grid max-w-3xl gap-4">
          {writable ? (
            <>
              <div className="grid gap-1 text-sm">
                <label htmlFor="weekly-plan-person">Person suchen</label>
                <Input
                  id="weekly-plan-person"
                  aria-label="Person"
                  autoComplete="off"
                  placeholder="Mindestens zwei Buchstaben"
                  value={personQuery}
                  onChange={(event) => setPersonQuery(event.target.value)}
                />
                {offered.length > 0 ? (
                  <ul
                    role="listbox"
                    aria-label="Kollegen"
                    className="max-h-56 overflow-y-auto rounded-md border bg-background p-1"
                  >
                    {offered.map((person) => (
                      <li key={person.id} role="option">
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-9 w-full justify-start"
                          onClick={() => addPerson(person)}
                        >
                          {person.displayName}
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <label className="grid gap-1 text-sm">
                <span className="flex items-baseline gap-2">
                  Freier Name
                  <span className="text-muted-foreground">ohne Konto</span>
                </span>
                <span className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Input
                    aria-label="Freier Name"
                    className="sm:min-w-0 sm:flex-1"
                    value={freeName}
                    onChange={(event) => setFreeName(event.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={addFreeName}
                  >
                    Freien Namen hinzufügen
                  </Button>
                </span>
              </label>
              {view === 'list' ? (
                <label className="grid gap-1 text-sm">
                  Neuer Gruppenname
                  <span className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input
                      aria-label="Neuer Gruppenname"
                      className="sm:min-w-0 sm:flex-1"
                      value={groupName}
                      onChange={(event) => setGroupName(event.target.value)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full sm:w-auto"
                      disabled={groupName.trim().length < 2}
                      onClick={addGroup}
                    >
                      Gruppe anlegen
                    </Button>
                  </span>
                </label>
              ) : null}
            </>
          ) : null}
          {canWriteWeek ? (
            <div className="flex flex-wrap gap-2">
              <Button type="button" onClick={() => void onSave()} disabled={savePlan.isPending}>
                Speichern
              </Button>
              {week.data.status === 'draft' ? (
                <Button
                  type="button"
                  onClick={() => void onPublish()}
                  disabled={publishPlan.isPending}
                >
                  Freigeben
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      <div
        data-testid="weekly-plan-toolbar"
        className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-2"
      >
        {week.data.picker.length > 0 ? (
          <label className="inline-flex items-center gap-2 text-sm">
            Woche
            <select
              aria-label="Woche"
              className="h-11 rounded-md border bg-background px-2"
              value={`${week.data.isoYear}-${week.data.isoWeek}`}
              onChange={(event) => {
                const [nextYear, nextWeek] = event.target.value.split('-');
                if (!nextYear || !nextWeek) {
                  return;
                }
                setSearchParams((current) => {
                  const next = new URLSearchParams(current);
                  next.set('jahr', nextYear);
                  next.set('woche', nextWeek);
                  return next;
                });
              }}
            >
              {week.data.picker.map((entry) => (
                <option
                  key={`${entry.isoYear}-${entry.isoWeek}`}
                  value={`${entry.isoYear}-${entry.isoWeek}`}
                >
                  {entry.status === 'draft' ? `KW ${entry.isoWeek} Entwurf` : `KW ${entry.isoWeek}`}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {week.data.registers.length > 0 ? (
          <Tabs
            value={activeRegister}
            onValueChange={setRegisterId}
            className="w-fit flex-row gap-0"
          >
            <TabsList aria-label="Standorte" className="h-11">
              {week.data.registers.map((register) => (
                <TabsTrigger key={register.siteId} value={register.siteId} className="h-9">
                  {register.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : null}
        <div role="group" aria-label="Ansicht" className="inline-flex rounded-md bg-muted p-1">
          <Button
            type="button"
            variant={view === 'time' ? 'default' : 'ghost'}
            aria-pressed={view === 'time'}
            className="min-h-11"
            onClick={() => setView('time')}
          >
            Planübersicht
          </Button>
          <Button
            type="button"
            variant={view === 'list' ? 'default' : 'ghost'}
            aria-pressed={view === 'list'}
            className="min-h-11"
            onClick={() => setView('list')}
          >
            Liste
          </Button>
        </div>
        {view === 'time' ? (
          <div role="group" aria-label="Zeitraum" className="inline-flex rounded-md bg-muted p-1">
            {(
              [
                ['today', 'Heute'],
                ['three', '3 Tage'],
                ['week', 'Woche'],
              ] as const
            ).map(([entry, label]) => (
              <Button
                key={entry}
                type="button"
                variant={range === entry ? 'default' : 'ghost'}
                aria-pressed={range === entry}
                className="min-h-11"
                onClick={() => setRange(entry)}
              >
                {label}
              </Button>
            ))}
          </div>
        ) : null}
        {view === 'list' ? (
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => {
              setPdfError(false);
              setPdfOpen(true);
            }}
          >
            PDF
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          aria-expanded={staffOpen}
          className="min-h-11"
          onClick={() => setStaffOpen((open) => !open)}
        >
          Mitarbeiter
        </Button>
        {ownRows[0] || selectedRows.length > 0 ? (
          <div role="group" aria-label="Mitarbeiter" className="flex flex-wrap items-center gap-2">
            {ownRows[0] ? (
              <span className="inline-flex min-h-11 items-center rounded-md bg-primary px-3 text-primary-foreground">
                {ownRows[0].displayName}
              </span>
            ) : null}
            {selectedRows.map((person) => (
              <span
                key={person.userId}
                className="inline-flex min-h-11 items-center gap-1 rounded-md border py-1 pr-3 pl-1"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 shrink-0"
                  aria-label={`${person.displayName} aus der Auswahl entfernen`}
                  onClick={() => removeStaff(person.userId)}
                >
                  <XIcon className="size-4" aria-hidden />
                </Button>
                <span>{person.displayName}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>
      {staffOpen && staffChoices.length > 8 ? (
        <Input
          aria-label="Mitarbeiter"
          autoComplete="off"
          value={staffQuery}
          onChange={(event) => setStaffQuery(event.target.value)}
        />
      ) : null}
      {staffOpen && visibleStaff.length > 0 ? (
        <ul
          role="listbox"
          aria-label="Mitarbeiter auf dem Plan"
          className="max-h-56 overflow-y-auto rounded-md border bg-background p-1"
        >
          {visibleStaff.map((person) => (
            <li key={person.userId} role="option">
              <Button
                type="button"
                variant="ghost"
                className="min-h-11 w-full justify-start"
                aria-label={`${person.displayName} hinzufügen`}
                onClick={() => addStaff(person.userId)}
              >
                {person.displayName}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {staffOpen && staffChoices.length === 0 ? (
        <p>Keine weiteren Personen auf diesem Plan.</p>
      ) : null}
      {timeHint ? <p>{timeHint}</p> : null}
      {view === 'time' && ownRows.length === 0 ? (
        <p>Keine eigene Zeile auf diesem Standort.</p>
      ) : null}
      {view === 'list' ? (
        <WeeklyPlanGroupList
          rows={registerRows}
          groups={siteGroups}
          columns={columnKeys}
          marked={marked}
          focusedDay={listFocus}
          onFocusDay={setDayFocus}
          writable={writable}
          meId={me.data?.id}
          selectedIds={selectedIds}
          recent={(row, day) => isRecentCell(week.data.rows, row, day)}
          patchRow={patchRow}
          removeRow={removeRow}
          moveRow={moveRow}
          moveGroup={moveGroup}
          renameGroup={(id, name) =>
            setGroups((current) =>
              current.map((group) => (group.id === id ? { ...group, name: name.trim() } : group)),
            )
          }
          deleteGroup={deleteGroup}
          assignGroup={assignGroup}
          dropOnRow={dropOnRow}
          reorderGroup={reorderGroup}
          addMembers={(groupId, people) => {
            for (const person of people) {
              addPerson(person, groupId);
            }
          }}
          colleagues={memberColleagues.data?.items ?? []}
          colleaguesPending={memberColleagues.isPending}
          memberQuery={memberQuery}
          onMemberQuery={setMemberQuery}
          onMembersOpen={setMembersOpen}
          planUserIds={planUserIds}
        />
      ) : null}
      {view === 'time' ? (
        <>
          <div className="grid gap-3 md:hidden" data-testid="weekly-plan-time-cards">
            {timeWithoutColumns || !timeFocus ? null : (
              <>
                <div role="group" aria-label="Tag" className="flex flex-wrap gap-1">
                  {visibleColumns.map((day) => (
                    <Button
                      key={day}
                      type="button"
                      variant="ghost"
                      aria-label={DAY_LABEL[day]}
                      aria-current={marked === day ? 'date' : undefined}
                      aria-pressed={timeFocus === day}
                      className={cn(
                        'min-h-11',
                        marked === day && 'bg-status-info text-status-info-foreground',
                      )}
                      onClick={() => setDayFocus(day)}
                    >
                      {DAY_SHORT[day]}
                    </Button>
                  ))}
                </div>
                {timeRows.map((row) => {
                  const mine = row.userId !== null && row.userId === me.data?.id;
                  return (
                    <article
                      key={row.key}
                      className={CARD_CLASS[personChrome(row, me.data?.id, selectedIds)]}
                      aria-label={mine ? 'Meine Woche' : rowTitle(row)}
                    >
                      <div className="flex items-start gap-2">
                        <h2 className="line-clamp-2 min-w-0 font-medium" title={row.displayName}>
                          {row.displayName}
                        </h2>
                        {writable ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="size-11 shrink-0"
                            aria-label={`${rowTitle(row)} entfernen`}
                            onClick={() => removeRow(row.key)}
                          >
                            <Trash2Icon className="size-4" aria-hidden />
                          </Button>
                        ) : null}
                      </div>
                      <p className="mt-2 text-sm">
                        <span className="text-muted-foreground">Einsatz als </span>
                        {assignmentField(row, writable, patchRow)}
                      </p>
                      <div className="mt-2">
                        {cellField(
                          row,
                          timeFocus,
                          writable,
                          isRecentCell(week.data?.rows ?? [], row, timeFocus),
                          patchRow,
                          'clamp',
                        )}
                      </div>
                    </article>
                  );
                })}
              </>
            )}
          </div>
          <div className="hidden min-w-0 md:block" data-testid="weekly-plan-time-grid">
            {timeWithoutColumns ? null : (
              <div>
                <div className="flex min-w-0">
                  <div className="w-44 shrink-0" />
                  <div className="flex min-w-0 flex-1">
                    {visibleColumns.map((day) => (
                      <div
                        key={day}
                        role="group"
                        aria-label={DAY_LABEL[day]}
                        aria-current={marked === day ? 'date' : undefined}
                        className={cn(
                          'min-w-0 flex-1 px-1 text-center',
                          dayHeaderClass(marked === day),
                        )}
                      >
                        {range === 'week' ? DAY_SHORT[day] : DAY_LABEL[day]}
                      </div>
                    ))}
                  </div>
                </div>
                {timeRows.map((row) => {
                  const mine = row.userId !== null && row.userId === me.data?.id;
                  const chrome = personChrome(row, me.data?.id, selectedIds);
                  return (
                    <div
                      key={row.key}
                      role="row"
                      aria-label={mine ? 'Meine Woche' : row.displayName}
                      className={cn('flex min-w-0', TIME_ROW_CLASS[chrome])}
                    >
                      <div className="w-44 shrink-0 px-2 py-2">
                        <div className="flex items-start gap-1">
                          <p className="line-clamp-2 min-w-0 font-medium" title={row.displayName}>
                            {row.displayName}
                          </p>
                          {writable ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="size-8 shrink-0"
                              aria-label={`${rowTitle(row)} entfernen`}
                              onClick={() => removeRow(row.key)}
                            >
                              <Trash2Icon className="size-4" aria-hidden />
                            </Button>
                          ) : null}
                        </div>
                        <p className="mt-1 text-sm">
                          <span className="text-muted-foreground">Einsatz als </span>
                          {assignmentField(row, writable, patchRow)}
                        </p>
                      </div>
                      <div className="flex min-w-0 flex-1">
                        {visibleColumns.map((day) => (
                          <div key={day} className="min-w-0 flex-1 px-1 py-2">
                            {cellField(
                              row,
                              day,
                              writable,
                              isRecentCell(week.data.rows, row, day),
                              patchRow,
                              'clamp',
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : null}
      <div role="group" aria-label="Farblegende" className="flex flex-wrap gap-4 text-sm">
        <p className="flex items-center gap-2">
          <span
            className="size-3 shrink-0 rounded-sm border border-status-info-border bg-status-info"
            aria-hidden
          />
          Heute
        </p>
        <p className="flex items-center gap-2">
          <span
            className="size-3 shrink-0 rounded-sm border border-status-warning-border bg-status-warning"
            aria-hidden
          />
          Geändert (24 Stunden)
        </p>
      </div>
      {week.data.history[0] ? (
        <section aria-label="Historie" className="min-w-0 space-y-2">
          <Button
            type="button"
            variant="outline"
            aria-expanded={historyOpen}
            className="min-h-11 justify-start gap-2"
            onClick={() => setHistoryOpen((open) => !open)}
          >
            {historyOpen ? (
              <ChevronDownIcon className="size-4" aria-hidden />
            ) : (
              <ChevronRightIcon className="size-4" aria-hidden />
            )}
            Historie
          </Button>
          {historyOpen ? (
            <Table aria-label="Änderungen">
              <TableHeader>
                <TableRow>
                  <TableHead>Standort</TableHead>
                  <TableHead>Person</TableHead>
                  <TableHead>Tag</TableHead>
                  <TableHead>Vorher</TableHead>
                  <TableHead>Nachher</TableHead>
                  <TableHead>Von</TableHead>
                  <TableHead>Wann</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {week.data.history.map((line) => (
                  <TableRow
                    key={`${line.at}-${line.siteId}-${line.personLabel}-${line.day}-${line.nextText}`}
                  >
                    <TableCell>{line.siteName}</TableCell>
                    <TableCell>{line.personLabel}</TableCell>
                    <TableCell>{DAY_LABEL[line.day]}</TableCell>
                    <TableCell>{line.previousText || '—'}</TableCell>
                    <TableCell>{line.nextText || '—'}</TableCell>
                    <TableCell>{line.actorName}</TableCell>
                    <TableCell>{berlinStamp(line.at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </section>
      ) : null}
      <AlertDialog
        open={blocker.state === 'blocked'}
        onOpenChange={(next) => {
          if (!next && blocker.state === 'blocked') {
            blocker.reset();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Änderungen an dieser Woche verwerfen?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                if (blocker.state === 'blocked') {
                  blocker.reset();
                }
              }}
            >
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                if (blocker.state === 'blocked') {
                  blocker.proceed();
                }
              }}
            >
              Verwerfen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog
        open={pdfOpen}
        onOpenChange={(open) => {
          setPdfOpen(open);
          if (!open) {
            setPdfError(false);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>PDF</DialogTitle>
          </DialogHeader>
          <p>{`KW ${week.data.isoWeek}`}</p>
          <p>{week.data.registers.find((register) => register.siteId === activeRegister)?.name}</p>
          <p>Exportiert die gespeicherte Gruppenreihenfolge.</p>
          {dirty ? <p>Ungespeicherte Änderungen sind nicht im PDF.</p> : null}
          {pdfError ? <p>PDF nicht verfügbar.</p> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPdfOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" disabled={pdfPending} onClick={() => void downloadPdf()}>
              Herunterladen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
